/* ==========================================================================
   WAVELESS — product.js
   PDP gallery (color grouping, crossfade arrows, mobile swipe slider,
   lightbox), sticky mobile add-to-cart, size-guide modal, and the
   match-set (bundle) form.

   All initializers are idempotent (dataset guards) and re-run on
   `shopify:section:load`, so the theme editor preview never stacks
   duplicate event listeners.
   ========================================================================== */
(function () {
  'use strict';

  const sliderMedia = window.matchMedia('(max-width: 1024px)');

  /* ─── Gallery: color filter + arrows + slider + thumbnails ─ */

  function initGallery(gallery) {
    if (gallery.dataset.galleryInit === '1') return;
    gallery.dataset.galleryInit = '1';

    const main = gallery.querySelector('.pdp-main-img');
    const prevBtn = gallery.querySelector('[data-gallery-prev]');
    const nextBtn = gallery.querySelector('[data-gallery-next]');

    // Raw option value → canonical color key, rendered server-side by
    // color-key.liquid. Single source of truth — no JS color mapping.
    let colorMap = {};
    const mapEl = gallery.querySelector('[data-color-map]');
    if (mapEl) {
      try { colorMap = JSON.parse(mapEl.textContent); } catch (e) { /* keep empty */ }
    }
    const showGeneral = gallery.dataset.showGeneral !== 'hide';

    const allItems = () => Array.from(gallery.querySelectorAll('.media-item'));
    // Only media matching the selected color (or all, when unfiltered)
    const visibleItems = () => allItems().filter((m) => !m.classList.contains('media-hidden'));

    function updateArrows() {
      const count = visibleItems().length;
      if (prevBtn) prevBtn.hidden = count < 2;
      if (nextBtn) nextBtn.hidden = count < 2;
    }

    function setActive(id, scroll) {
      allItems().forEach((m) => m.classList.toggle('active', m.dataset.mediaId === id));
      gallery.querySelectorAll('.pdp-thumb').forEach((t) => t.classList.toggle('sel', t.dataset.mediaTarget === id));
      // On mobile the gallery is a scroll-snap slider — swipe to the item.
      if (scroll && main && sliderMedia.matches) {
        const idx = visibleItems().findIndex((m) => m.dataset.mediaId === id);
        if (idx > -1) main.scrollTo({ left: idx * main.clientWidth, behavior: 'smooth' });
      }
    }
    gallery.setActiveMedia = setActive;

    /*
     * Color grouping: a media item belongs to a color when its
     * data-color slug (from the alt-text "color:…" convention, the
     * variant image assignment, or a plain color-name alt — resolved
     * server-side) equals the selected color's canonical slug.
     * "all" = general image (shown per section setting).
     * Zero tagged images for the color → the FULL gallery stays
     * visible as fallback.
     */
    gallery.filterMedia = function (raw) {
      const key = raw ? (colorMap[raw] || raw) : '';
      const items = allItems();
      const matches = key ? items.filter((m) => m.dataset.color === key) : [];
      let show;
      if (!matches.length) {
        show = items;
      } else {
        show = items.filter((m) => m.dataset.color === key || (showGeneral && m.dataset.color === 'all'));
      }
      items.forEach((m) => m.classList.toggle('media-hidden', !show.includes(m)));
      gallery.querySelectorAll('.pdp-thumb').forEach((t) => {
        const item = items.find((m) => m.dataset.mediaId === t.dataset.mediaTarget);
        t.classList.toggle('media-hidden', item ? item.classList.contains('media-hidden') : false);
      });
      const active = gallery.querySelector('.media-item.active');
      if (!active || active.classList.contains('media-hidden')) {
        const first = visibleItems()[0];
        if (first) setActive(first.dataset.mediaId, true);
      }
      updateArrows();
    };

    // Arrow navigation cycles through the VISIBLE media set.
    function step(dir) {
      const vis = visibleItems();
      if (vis.length < 2) return;
      const current = vis.findIndex((m) => m.classList.contains('active'));
      const next = vis[(current + dir + vis.length) % vis.length];
      setActive(next.dataset.mediaId, true);
    }
    if (prevBtn) prevBtn.addEventListener('click', () => step(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => step(1));
    gallery.addEventListener('keydown', (e) => {
      if (e.target.matches('input, select, textarea')) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
    });

    gallery.addEventListener('click', (e) => {
      const thumb = e.target.closest('.pdp-thumb');
      if (thumb) setActive(thumb.dataset.mediaTarget, true);
      if (e.target.closest('[data-zoom]')) {
        const active = gallery.querySelector('.media-item.active img');
        const lightbox = document.getElementById('MediaLightbox');
        if (active && lightbox) {
          const img = lightbox.querySelector('img');
          img.src = active.dataset.full || active.src;
          img.alt = active.alt || '';
          lightbox.classList.add('open');
        }
      }
    });

    // Swiping the slider highlights the matching thumbnail.
    if (main) {
      let scrollTimer;
      main.addEventListener('scroll', () => {
        if (!sliderMedia.matches) return;
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const idx = Math.round(main.scrollLeft / main.clientWidth);
          const item = visibleItems()[idx];
          if (item) setActive(item.dataset.mediaId, false);
        }, 80);
      }, { passive: true });
    }

    updateArrows();
  }

  /* ─── Lightbox close (document delegation — editor-safe) ──── */

  if (!document.documentElement.dataset.wlLightboxInit) {
    document.documentElement.dataset.wlLightboxInit = '1';
    document.addEventListener('click', (e) => {
      const lightbox = e.target.closest('.lightbox');
      if (lightbox && (e.target === lightbox || e.target.closest('.lightbox-close'))) {
        lightbox.classList.remove('open');
      }
    });

    /* Size guide / generic modal (delegated as well) */
    document.addEventListener('click', (e) => {
      const openBtn = e.target.closest('[data-open-modal]');
      if (openBtn) {
        const modal = document.getElementById(openBtn.dataset.openModal);
        if (modal) modal.classList.add('open');
      }
      const modal = e.target.closest('.wl-modal');
      if (modal && (e.target === modal || e.target.closest('.close-btn'))) {
        modal.classList.remove('open');
      }
    });
  }

  /* ─── Sticky mobile add-to-cart ─────────────────────────── */

  function initSticky(root) {
    const sticky = (root || document).querySelector('#StickyATC');
    const mainCta = document.querySelector('[data-main-cta]');
    if (!sticky || !mainCta || sticky.dataset.stickyInit === '1') return;
    if (!('IntersectionObserver' in window)) return;
    sticky.dataset.stickyInit = '1';
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          sticky.classList.toggle('show', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { threshold: 0 }
    );
    observer.observe(mainCta);
    sticky.querySelector('button').addEventListener('click', () => {
      const form = document.querySelector('form[data-type="add-to-cart-form"]');
      if (form) form.requestSubmit();
    });
  }

  /* ─── Init + theme-editor re-init (no duplicate listeners) ── */

  function initAll(root) {
    (root || document).querySelectorAll('[data-gallery]').forEach(initGallery);
    initSticky(root ? root.parentElement || document : document);
  }

  initAll(document);
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));

  /* ─── Match set (bundle) form ───────────────────────────── */
  /*
   * <bundle-form> contains one [data-bundle-component] per component
   * product, each with its own option <select>s and a variants JSON blob.
   * The set is purchasable only when EVERY selected component variant is
   * available. Adding to cart posts all real component variants in one
   * /cart/add.js call — Shopify then deducts each component's inventory
   * natively at checkout. No shadow inventory, no frontend simulation.
   */
  class BundleForm extends HTMLElement {
    connectedCallback() {
      this.components = Array.from(this.querySelectorAll('[data-bundle-component]')).map((el) => {
        let variants = [];
        try { variants = JSON.parse(el.querySelector('[data-variants]').textContent); } catch (e) { /* noop */ }
        return { el: el, variants: variants, selected: null };
      });
      this.button = this.querySelector('[data-bundle-add]');
      this.totalEl = this.querySelector('[data-bundle-total]');
      this.originalEl = this.querySelector('[data-bundle-original]');
      this.errorEl = this.querySelector('[data-bundle-error]');
      this.bundleId = this.dataset.bundleId;
      this.bundleTitle = this.dataset.bundleTitle;
      // Fixed saving rendered server-side from the set product's own price.
      // Mirrors the automatic discount, so this total matches cart and checkout.
      this.savings = parseInt(this.dataset.bundleSavings, 10) || 0;

      this.addEventListener('change', (e) => {
        const comp = e.target.closest('[data-bundle-component]');
        if (comp) this.resolveComponent(comp);
        this.updateState();
      });
      if (this.button) this.button.addEventListener('click', this.addToCart.bind(this));

      this.components.forEach((c) => this.resolveComponent(c.el));
      this.updateState();
    }

    resolveComponent(el) {
      const comp = this.components.find((c) => c.el === el);
      if (!comp) return;
      // Read <select> AND hidden <input> option controls: preconfigured sets
      // (Light/Dark) lock the colour as a hidden input instead of a select.
      const inputs = Array.from(el.querySelectorAll('[data-option-index]'));
      const chosen = [];
      inputs.forEach((s) => { chosen[parseInt(s.dataset.optionIndex, 10)] = s.value; });
      comp.selected = comp.variants.find((v) => chosen.every((val, i) => v.options[i] === val)) || null;

      // availability indicator + variant image swap
      const availEl = el.querySelector('[data-availability]');
      const available = !!(comp.selected && comp.selected.available);
      el.classList.toggle('unavailable', !available);
      if (availEl) {
        availEl.querySelector('.txt').textContent = available
          ? availEl.dataset.inStock
          : (comp.selected ? availEl.dataset.soldOut : availEl.dataset.unavailable);
      }
      const img = el.querySelector('.thumb img');
      if (img && comp.selected && comp.selected.featured_image) {
        img.src = comp.selected.featured_image.src + (comp.selected.featured_image.src.includes('?') ? '&' : '?') + 'width=180';
        img.srcset = '';
      }
      const priceEl = el.querySelector('[data-component-price]');
      if (priceEl && comp.selected) priceEl.textContent = window.formatMoney(comp.selected.price);
    }

    updateState() {
      const allAvailable = this.components.length > 0 && this.components.every((c) => c.selected && c.selected.available);
      const sum = this.components.reduce((acc, c) => acc + (c.selected ? c.selected.price : 0), 0);
      const discounted = Math.max(0, sum - this.savings);
      if (this.totalEl) this.totalEl.textContent = window.formatMoney(discounted);
      if (this.originalEl) {
        this.originalEl.textContent = window.formatMoney(sum);
        this.originalEl.hidden = this.savings <= 0;
      }
      if (this.button) {
        this.button.toggleAttribute('disabled', !allAvailable);
        this.button.setAttribute('aria-disabled', String(!allAvailable));
        const label = this.button.querySelector('[data-atc-label]') || this.button;
        label.textContent = allAvailable
          ? this.button.dataset.labelAdd
          : window.theme.strings.bundleUnavailable;
      }
      if (this.errorEl) this.errorEl.hidden = true;
    }

    async addToCart(e) {
      e.preventDefault();
      if (this.button.hasAttribute('disabled')) return;
      this.button.classList.add('loading');
      const setRef = this.bundleId + '-' + Date.now();
      const items = this.components.map((c) => ({
        id: c.selected.id,
        quantity: 1,
        properties: { _set: setRef, _set_name: this.bundleTitle }
      }));
      try {
        const res = await fetch(window.theme.routes.cartAdd + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        });
        const data = await res.json();
        if (data.status && data.status !== 200) {
          // e.g. insufficient stock detected server-side between render and add
          if (this.errorEl) {
            this.errorEl.textContent = data.description || window.theme.strings.cartError;
            this.errorEl.hidden = false;
          }
          return;
        }
        await window.refreshCart();
        window.showToast(window.theme.strings.addedToBag);
        setTimeout(() => window.openCartDrawer && window.openCartDrawer(), 350);
      } catch (err) {
        if (this.errorEl) {
          this.errorEl.textContent = window.theme.strings.cartError;
          this.errorEl.hidden = false;
        }
      } finally {
        this.button.classList.remove('loading');
      }
    }
  }
  customElements.define('bundle-form', BundleForm);
})();
